import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit'

import {
  ActivityIdentity,
  Attempt,
  LearningPosition,
  LearningSurface,
  OracleContext,
  QuestionExtras,
  QuestionIdentity,
} from '~/aioracle/types/learning-context.types'
import { logout } from '~/auth/slices/auth.slice'
import { RootState } from '~/redux/store'

const MAX_ATTEMPTS = 20

interface LearningContextState extends OracleContext {
  attempts: Attempt[]
  /** Owner token for the guarded clears. Internal - stripped by the wire selector. */
  contextId?: string
  /** `${activityId}:${difficultyLevel}` that accumulated wrong attempts. Internal - stripped by the
   *  wire selector. Deliberately survives identity clears: the comeback must outlive modal close/reopen. */
  comebackOwner?: string
}

const initialState: LearningContextState = { surface: 'other', attempts: [] }

const sameActivity = (a: QuestionIdentity, b: QuestionIdentity): boolean =>
  a.source === b.source && a.activityId === b.activityId && a.difficultyLevel === b.difficultyLevel

const sameOptionKeys = (a: string[] = [], b: string[] = []): boolean =>
  a.length === b.length && a.every((key, index) => key === b[index])

const slice = createSlice({
  name: 'learningContext',
  initialState,
  reducers: {
    /** Position only: pages re-dispatch on every refetch, so touching identity or attempts wipes them. */
    setLearningPosition: (state, action: PayloadAction<LearningPosition>) => {
      // A same-owner refresh preserves identity because the activity may be open; a new owner starts
      // from initialState, so no position key is inherited by the next page.
      const sameOwner = state.contextId === action.payload.contextId && state.source === action.payload.source
      // Default surface is 'content' — never 'other', which the wire selector suppresses. An open
      // activity survives only a same-owner content refresh: a browse surface means its page is gone.
      const requested = action.payload.surface ?? 'content'
      return {
        ...(sameOwner ? state : initialState),
        ...action.payload,
        surface: state.surface === 'activity' && sameOwner && requested === 'content' ? 'activity' : requested,
      }
    },

    /** No-ops without a position: a modal unmounting after its page cleared must not resurrect one. */
    setLearningSurface: (state, action: PayloadAction<LearningSurface>) =>
      state.source ? { ...state, surface: action.payload } : state,

    setActivityIdentity: (state, action: PayloadAction<ActivityIdentity>) => {
      const changed =
        state.activityId !== action.payload.activityId || state.difficultyLevel !== action.payload.difficultyLevel
      // `onClickNextSet` changes difficultyLevel without unmounting, so the question must clear with it.
      if (!changed) return { ...state, ...action.payload }
      return {
        ...state,
        ...action.payload,
        questionId: undefined,
        attempts: [],
        instance: undefined,
        displayedOptionKeys: undefined,
        optionsLayout: undefined,
        displayedPairKeys: undefined,
      }
    },

    /** Dispatched before any Check, so an unanswered question is reportable. Installs the activity
     *  fields rather than guarding on them: child effects run first, so the question arrives first. */
    setCurrentQuestion: (state, action: PayloadAction<QuestionIdentity & QuestionExtras>) => {
      const {
        instanceTask,
        visibleItem,
        instanceImageUrl,
        instanceExpectedAnswer,
        instanceFacts,
        displayedOptionKeys,
        optionsLayout,
        displayedPairKeys,
        ...identity
      } = action.payload
      // expectedAnswer never creates an instance by itself: it only ever accompanies a task.
      const extras = {
        instance:
          instanceTask || visibleItem || instanceImageUrl
            ? {
                task: instanceTask,
                visibleItem,
                imageUrl: instanceImageUrl,
                expectedAnswer: instanceExpectedAnswer,
                facts: instanceFacts,
              }
            : undefined,
        displayedOptionKeys,
        optionsLayout,
        displayedPairKeys,
      }
      if (state.questionId === identity.questionId) {
        // Interactive regenerates its instance under an unchanged key, so new extras must be adopted.
        // Reference-stable when nothing changed: this feeds a memoized selector.
        const same =
          state.instance?.task === extras.instance?.task &&
          state.instance?.visibleItem === extras.instance?.visibleItem &&
          state.instance?.imageUrl === extras.instance?.imageUrl &&
          state.instance?.expectedAnswer === extras.instance?.expectedAnswer &&
          sameOptionKeys(state.instance?.facts, extras.instance?.facts) &&
          sameOptionKeys(state.displayedOptionKeys, displayedOptionKeys) &&
          state.optionsLayout === optionsLayout &&
          sameOptionKeys(state.displayedPairKeys, displayedPairKeys)
        if (same) return state
        // A regenerated instance after a correct attempt is a new figure: the correct attempt belonged to
        // the old one, and the backend drops its guard and facts while the latest attempt reads correct.
        const solvedPrevious = state.attempts.at(-1)?.isCorrect === true
        return { ...state, ...extras, attempts: solvedPrevious ? [] : state.attempts }
      }
      return {
        ...state,
        source: identity.source ?? state.source,
        activityId: identity.activityId,
        difficultyLevel: identity.difficultyLevel,
        questionId: identity.questionId,
        attempts: [],
        ...extras,
      }
    },

    /** Carries its own identity because the modal's `setCurrentQuestionId` is async, so a graded attempt
     *  can arrive first. Another question of the same activity is adopted; another activity is stale. */
    recordAttempt: (state, action: PayloadAction<{ identity: QuestionIdentity; attempt: Attempt }>) => {
      const { identity, attempt } = action.payload
      if (!sameActivity(state, identity)) return state
      // A correct attempt is also a freshness boundary: interactive regenerates its instance under the
      // same key, and must not inherit the previous instance's wrong answers.
      const lastWasCorrect = state.attempts[state.attempts.length - 1]?.isCorrect === true
      const fresh = state.questionId !== identity.questionId || lastWasCorrect
      const owner = `${identity.activityId}:${identity.difficultyLevel}`
      const next: LearningContextState = {
        ...state,
        questionId: identity.questionId,
        attempts: (fresh ? [attempt] : [...state.attempts, attempt]).slice(-MAX_ATTEMPTS),
      }
      if (attempt.isCorrect === false) next.comebackOwner = owner
      // A matching correct attempt resolves the comeback silently — the activity already graded it.
      else if (attempt.isCorrect === true && state.comebackOwner === owner) delete next.comebackOwner
      return next
    },

    /** The chat verdict celebrated this comeback; the activity's later entry must not re-celebrate. */
    consumeComeback: (state) => {
      const { comebackOwner, ...rest } = state
      return rest as LearningContextState
    },

    setAutoTriggered: (state) => ({ ...state, wasAutoTriggered: true }),

    /** Deletes the key rather than setting false, so later turns carry nothing at all. */
    consumeAutoTrigger: (state) => {
      const { wasAutoTriggered, ...rest } = state
      return rest as LearningContextState
    },

    /** The modal's unmount. A level change that closes the activity is a same-owner refresh, which
     *  preserves identity, so without this the closed activity stayed on the wire. Owner-guarded. */
    clearActivityIdentity: (state, action: PayloadAction<{ activityId?: string }>) =>
      state.activityId && state.activityId === action.payload.activityId
        ? {
            ...state,
            activityId: undefined,
            difficultyLevel: undefined,
            questionId: undefined,
            attempts: [],
            instance: undefined,
            displayedOptionKeys: undefined,
            optionsLayout: undefined,
            displayedPairKeys: undefined,
          }
        : state,

    /** Guarded by the owner token, not source: React mounts the new page before unmounting the old. */
    clearLearningContext: (state, action: PayloadAction<{ contextId: string } | undefined>) =>
      action.payload && action.payload.contextId !== state.contextId ? state : initialState,
  },
  extraReducers: (builder) => builder.addCase(logout, () => initialState),
})

export const selectLearningContext = (state: RootState): LearningContextState => state.learningContext
export const selectIsActivitySurface = (state: RootState): boolean => state.learningContext.surface === 'activity'

/** Memoized: the chat panel selects this by reference, so a new object per action would re-render it. */
export const selectOracleContextPayload = createSelector(
  [selectLearningContext],
  (context): OracleContext | undefined => {
    if (context.surface === 'other' || !context.source) return undefined
    const { attempts, contextId, comebackOwner, ...rest } = context
    return attempts.length ? { ...rest, attempts } : rest
  },
)

export const {
  setLearningPosition: setLearningPositionActionCreator,
  setLearningSurface: setLearningSurfaceActionCreator,
  setActivityIdentity: setActivityIdentityActionCreator,
  clearActivityIdentity: clearActivityIdentityActionCreator,
  setCurrentQuestion: setCurrentQuestionActionCreator,
  recordAttempt: recordAttemptActionCreator,
  consumeComeback: consumeComebackActionCreator,
  setAutoTriggered: setLearningContextAutoTriggeredActionCreator,
  consumeAutoTrigger: consumeAutoTriggerActionCreator,
  clearLearningContext: clearLearningContextActionCreator,
} = slice.actions

export default slice.reducer
